// ============================================
// TYPE DEFINITIONS
// ============================================

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean
  message: string
  data: T
  errors?: Record<string, string>
  pagination?: PaginationInfo
}

export interface PaginationInfo {
  total: number
  page: number
  per_page: number
  total_pages: number
  has_more: boolean
}

// Prodotto (da API prodotti)
export type ProfiloFamiglia = 'Small' | 'Medium' | 'Large'

export interface Prodotto {
  id: number
  nome: string
  codice: string
  commodity: 'luce' | 'gas'
  tipo_cliente: 'domestico' | 'business'
  profilo_famiglia?: ProfiloFamiglia
  cte_filename: string
  attivo: boolean
  created_at: string
}

// Session types - GDPR Compliant
export interface Session {
  id: string
  device_type: 'desktop' | 'tablet' | 'mobile'
  browser: string
  os: string
  started_at: string
  last_activity_at: string
  is_active: boolean
  // GDPR fields
  mode: 'self' | 'operator'
  timeout_minutes: number
  expires_at: string
  time_remaining?: SessionTimeRemaining
}

// Time remaining for session expiration
export interface SessionTimeRemaining {
  seconds: number
  minutes: number
  expired: boolean
}

// Session create response
export interface SessionCreateResponse {
  session_id: string
  device_type: 'desktop' | 'tablet' | 'mobile'
  started_at: string
  mode: 'self' | 'operator'
  timeout_minutes: number
  expires_at: string
  time_remaining: SessionTimeRemaining
}

// Session renew response
export interface SessionRenewResponse {
  renewed: boolean
  session_id: string
  expires_at: string
  time_remaining: SessionTimeRemaining
}

// Tipo cliente
export type TipoCliente = 'domestico' | 'business'

// Form data types
export interface FormData {
  // Tipo cliente (self-service selection, operator via route)
  tipo_cliente: TipoCliente

  // Anagrafica
  nome: string
  cognome: string
  codice_fiscale: string
  data_nascita: string
  luogo_nascita: string
  provincia_nascita: string
  telefono: string
  email: string
  pec: string

  // Business - Dati Azienda
  ragione_sociale: string
  partita_iva: string
  codice_fiscale_azienda: string
  codice_sdi: string
  pec_azienda: string

  // Business - Sede Legale
  sede_legale_indirizzo: string
  sede_legale_civico: string
  sede_legale_cap: string
  sede_legale_citta: string
  sede_legale_provincia: string

  // Business - Rappresentante Legale
  rappresentante_legale_nome: string
  rappresentante_legale_cognome: string
  rappresentante_legale_cf: string

  // Business - Referente Azienda
  referente_azienda_nome: string
  referente_azienda_telefono: string

  // Business - Opzioni
  grande_impresa: boolean
  agevolazione_iva: string
  agevolazione_accise: string
  invio_fattura: string

  // Business - Scheda Tecnica
  codice_ateco: string
  tipo_attivita: string
  iva_aliquota_ee: '22' | '10' | 'esenzione' | null
  iva_aliquota_gas: '22' | '10' | 'esenzione' | null
  accisa_usi_industriali: boolean
  tensione_fornitura: 'bt' | 'mt' | null
  provenienza_mercato: 'libero' | 'regolamentato' | null
  numero_addetti: number | null

  // Indirizzo residenza
  indirizzo_residenza: string
  cap_residenza: string
  citta_residenza: string
  provincia_residenza: string
  
  // Indirizzo fornitura
  stesso_indirizzo: boolean
  indirizzo_fornitura: string
  cap_fornitura: string
  citta_fornitura: string
  provincia_fornitura: string
  
  // Tipo fornitura
  tipo_fornitura: 'luce' | 'gas' | 'entrambi'
  
  // Dati luce
  ha_luce: boolean
  pod_luce: string
  potenza_impegnata: number | null
  consumo_annuo_luce: number | null
  profilo_ee: string
  convenzione_ee: string
  data_attivazione_ee: string
  pod_titolarita: 'si' | 'no'
  tipo_attivazione_luce: TipoAttivazioneLuce | null
  fornitore_attuale_luce: string
  voltura_luce_accettata: boolean
  voltura_mortis_causa_luce: boolean
  mortis_causa_data_decesso_luce: string
  mortis_causa_residenza_6_mesi_luce: boolean
  vecchio_intestatario_luce: {
    nome: string
    cognome: string
    codice_fiscale: string
  } | null
  
  // Dati gas
  ha_gas: boolean
  pdr_gas: string
  matricola_contatore: string
  remi_pdc: string
  consumo_annuo_gas: number | null
  profilo_gas: string
  convenzione_gas: string
  data_attivazione_gas: string
  pdr_titolarita: 'si' | 'no'
  tipo_attivazione_gas: TipoAttivazioneGas | null
  fornitore_attuale_gas: string
  voltura_gas_accettata: boolean
  voltura_mortis_causa_gas: boolean
  mortis_causa_data_decesso_gas: string
  mortis_causa_residenza_6_mesi_gas: boolean
  vecchio_intestatario_gas: {
    nome: string
    cognome: string
    codice_fiscale: string
  } | null
  categoria_uso_gas: CategoriaUsoGas[]
  
  // Titolarita Immobile (solo per voltura)
  titolarita_immobile: TipoTitolaritaImmobile | null
  catasto_sezione: string
  catasto_foglio: string
  catasto_particella: string
  catasto_subalterno: string
  titolarita_note: string
  // Dati Locazione (per locatario/comodatario)
  locazione_agenzia_entrate: string
  locazione_data_registrazione: string
  locazione_numero: string
  
  // Prodotti (ID dal database)
  prodotto_luce_id: number | null
  prodotto_gas_id: number | null
  // Retrocompatibilità (codice prodotto)
  prodotto_luce: string | null
  prodotto_gas: string | null
  // VAS "Hera NoProblem": add-on selezionabile dall'operatore (Adventure) in fornitura
  vas_luce: boolean
  vas_gas: boolean
  
  // Company (heracomm/estenergy) - derivata dal comune di fornitura
  company: 'heracomm' | 'estenergy' | null
  
  // Pagamento
  /** null = operatore deve ancora scegliere tra bollettino PagoPA e addebito IBAN */
  metodo_pagamento: 'pagoPA' | 'iban' | null
  iban: string
  /** BIC/SWIFT, obbligatorio con IBAN estero (non IT). Solo agenzie abilitate. */
  bic_swift: string
  iban_intestato_a_me: boolean
  intestatario_iban: {
    nome: string
    cognome: string
    codice_fiscale: string
  } | null
  
  // Consensi
  consenso_privacy: boolean
  consenso_marketing: boolean
  consenso_comunicazioni: boolean
  
  // Richiesta esecuzione immediata (per subentro/voltura)
  richiesta_esecuzione_immediata: boolean
  
  // Modalita invio bolletta
  modalita_invio_bolletta: ModalitaInvioBolletta
  invio_indirizzo_tipo: TipoIndirizzoInvio
  invio_indirizzo_altro: string
  invio_cap_altro: string
  invio_citta_altro: string
  invio_provincia_altro: string
  invio_email_tipo: TipoEmailInvio
  invio_email_altro: string
  
  // Documento identità
  tipo_documento: 'carta_identita' | 'patente' | 'passaporto' | null
  numero_documento: string
  data_emissione_documento: string
  data_scadenza_documento: string
  ente_rilascio: string
  
  // Diario dei Consumi
  diario_consumi_attivo: boolean
  superficie_abitazione: number | null
  numero_abitanti: number | null
  tipologia_abitazione: 'appartamento' | 'casa_indipendente' | null
  proprietario_abitazione: boolean | null

  // Modalita di Contatto (sezione SEPA del plico)
  // Documenta come il cliente ha preso contatto con il consulente
  modalita_contatto: 'di_persona' | 'telefonato_poi_incontrato' | 'chiamato_poi_incontrato' | null

  // Flusso operatore: l'operatore non ha il documento del cliente,
  // sara il cliente a caricare fronte/retro durante la firma.
  client_must_upload_identity: boolean

  // Duplica pratica: ID della pratica originale completata da cui questa
  // deriva. Passato alla POST /submissions per collegare la nuova pratica
  // all'originale ed ereditarne l'ordine SiDial (aggiornamento vs creazione).
  duplicated_from_submission_id?: number | null
}

// Default form values
export const defaultFormData: FormData = {
  tipo_cliente: 'domestico',

  nome: '',
  cognome: '',
  codice_fiscale: '',
  data_nascita: '',
  luogo_nascita: '',
  provincia_nascita: '',
  telefono: '',
  email: '',
  pec: '',

  // Business defaults
  ragione_sociale: '',
  partita_iva: '',
  codice_fiscale_azienda: '',
  codice_sdi: '',
  pec_azienda: '',
  sede_legale_indirizzo: '',
  sede_legale_civico: '',
  sede_legale_cap: '',
  sede_legale_citta: '',
  sede_legale_provincia: '',
  rappresentante_legale_nome: '',
  rappresentante_legale_cognome: '',
  rappresentante_legale_cf: '',
  referente_azienda_nome: '',
  referente_azienda_telefono: '',
  grande_impresa: false,
  agevolazione_iva: '',
  agevolazione_accise: '',
  invio_fattura: '',
  codice_ateco: '',
  tipo_attivita: '',
  iva_aliquota_ee: null,
  iva_aliquota_gas: null,
  accisa_usi_industriali: false,
  tensione_fornitura: null,
  provenienza_mercato: null,
  numero_addetti: null,

  indirizzo_residenza: '',
  cap_residenza: '',
  citta_residenza: '',
  provincia_residenza: '',
  
  stesso_indirizzo: true,
  indirizzo_fornitura: '',
  cap_fornitura: '',
  citta_fornitura: '',
  provincia_fornitura: '',
  
  tipo_fornitura: 'luce',
  
  ha_luce: true,
  pod_luce: '',
  potenza_impegnata: null,
  consumo_annuo_luce: null,
  profilo_ee: '',
  convenzione_ee: '',
  data_attivazione_ee: '',
  pod_titolarita: 'si',
  tipo_attivazione_luce: null,
  fornitore_attuale_luce: '',
  voltura_luce_accettata: false,
  voltura_mortis_causa_luce: false,
  mortis_causa_data_decesso_luce: '',
  mortis_causa_residenza_6_mesi_luce: false,
  vecchio_intestatario_luce: null,
  
  ha_gas: false,
  pdr_gas: '',
  matricola_contatore: '',
  remi_pdc: '',
  consumo_annuo_gas: null,
  profilo_gas: '',
  convenzione_gas: '',
  data_attivazione_gas: '',
  pdr_titolarita: 'si',
  tipo_attivazione_gas: null,
  fornitore_attuale_gas: '',
  voltura_gas_accettata: false,
  voltura_mortis_causa_gas: false,
  mortis_causa_data_decesso_gas: '',
  mortis_causa_residenza_6_mesi_gas: false,
  vecchio_intestatario_gas: null,
  categoria_uso_gas: [],
  
  // Titolarita Immobile
  titolarita_immobile: null,
  catasto_sezione: '',
  catasto_foglio: '',
  catasto_particella: '',
  catasto_subalterno: '',
  titolarita_note: '',
  // Dati Locazione (per locatario/comodatario)
  locazione_agenzia_entrate: '',
  locazione_data_registrazione: '',
  locazione_numero: '',
  
  // Prodotti
  prodotto_luce_id: null,
  prodotto_gas_id: null,
  prodotto_luce: null,
  prodotto_gas: null,
  vas_luce: false,
  vas_gas: false,
  
  company: null,
  
  metodo_pagamento: null,
  iban: '',
  bic_swift: '',
  iban_intestato_a_me: true,
  intestatario_iban: null,
  
  consenso_privacy: false,
  consenso_marketing: false,
  consenso_comunicazioni: false,
  
  richiesta_esecuzione_immediata: false,
  
  // Modalita invio bolletta
  modalita_invio_bolletta: 'email',
  invio_indirizzo_tipo: 'residenza',
  invio_indirizzo_altro: '',
  invio_cap_altro: '',
  invio_citta_altro: '',
  invio_provincia_altro: '',
  invio_email_tipo: 'anagrafica',
  invio_email_altro: '',
  
  tipo_documento: null,
  numero_documento: '',
  data_emissione_documento: '',
  data_scadenza_documento: '',
  ente_rilascio: '',
  
  // Diario dei Consumi (sempre attivo)
  diario_consumi_attivo: true,
  superficie_abitazione: null,
  numero_abitanti: null,
  tipologia_abitazione: null,
  proprietario_abitazione: null,

  // Modalita di Contatto
  modalita_contatto: null,

  client_must_upload_identity: false,
}

// OCR extracted data
export interface OcrData {
  // Anagrafica
  nome?: string
  cognome?: string
  codice_fiscale?: string
  
  // Indirizzo
  indirizzo?: string
  cap?: string
  citta?: string
  provincia?: string
  
  // Luce
  pod?: string
  potenza_impegnata?: string
  consumo_annuo_luce?: string
  
  // Gas
  pdr?: string
  matricola_contatore?: string
  remi_pdc?: string
  consumo_annuo_gas?: string
  
  // Meta
  fornitore?: string
  tipo_bolletta?: 'luce' | 'gas' | 'entrambi'
  
  // Qualsiasi altro campo estratto
  [key: string]: string | undefined
}

// Upload response
export interface UploadResponse {
  file_id: number | null
  extractor: string
  fornitore: string
  data: OcrData
  processing_time_ms: number
}

// Tipo attivazione
// Luce: cambio_fornitore, cambio_fornitore_voltura, subentro
// Gas: cambio_fornitore, cambio_fornitore_voltura, subentro
export type TipoAttivazioneLuce = 'subentro' | 'cambio_fornitore_voltura' | 'cambio_fornitore'
export type TipoAttivazioneGas = 'subentro' | 'cambio_fornitore_voltura' | 'cambio_fornitore'
export type TipoAttivazione = TipoAttivazioneLuce | TipoAttivazioneGas

// Tipo documento identità
export type TipoDocumento = 'carta_identita' | 'patente' | 'passaporto'

// Categoria d'uso Gas (aggiornato)
export type CategoriaUsoGas = 'cottura' | 'acqua_calda' | 'riscaldamento_autonomo' | 'riscaldamento_centralizzato'

// Titolarita Immobile (per voltura)
export type TipoTitolaritaImmobile = 'proprietario' | 'locatario' | 'locatario_edilizia_pubblica' | 'comodatario' | 'usufruttuario' | 'altro'

// Modalita Invio Bolletta
export type ModalitaInvioBolletta = 'email' | 'posta'

// Tipo Indirizzo Invio (per posta)
export type TipoIndirizzoInvio = 'residenza' | 'fornitura' | 'altro'

// Tipo Email Invio (per email)
export type TipoEmailInvio = 'anagrafica' | 'altro'

// Vecchio intestatario (per voltura)
export interface VecchioIntestatario {
  nome: string
  cognome: string
  codice_fiscale: string
}

// Identity Document Info (senza image_data per performance)
export interface IdentityDocumentInfo {
  id: number
  submission_id: number | null
  image_type: 'fronte' | 'retro' | 'pagina' | 'altro'
  image_order: number
  original_filename: string | null
  mime_type: string | null
  file_size: number | null
  ocr_success: boolean
  tipo_documento: string | null
  created_at: string
}

// Stato documenti identità per sessione
export interface IdentityDocumentsStatus {
  documents: IdentityDocumentInfo[]
  count: number
  image_count: number
  has_pdf: boolean
  has_required_documents: boolean
  requirement: {
    description: string
    min_images: number
    or_pdf: boolean
  }
}

// Documento PDF generato
export interface GeneratedDocument {
  success: boolean
  type: 'subentro' | 'voltura' | 'cambio_fornitore'
  utility: 'luce' | 'gas'
  filename: string
  url: string
  fields_filled?: number
}

// Risposta generazione PDF
export interface GeneratePdfResponse {
  documents: GeneratedDocument[]
  submission_id: number
}

// Submission types
export interface Submission {
  id: number
  uuid: string
  status: 'draft' | 'pending' | 'completed' | 'cancelled'
  tipo_cliente: TipoCliente
  tipo_fornitura: 'luce' | 'gas' | 'entrambi'
  anagrafica: {
    nome: string | null
    cognome: string | null
    codice_fiscale: string | null
    telefono: string | null
    email: string | null
  }
  indirizzo: {
    residenza: {
      indirizzo: string | null
      cap: string | null
      citta: string | null
      provincia: string | null
    }
    fornitura: {
      indirizzo: string | null
      cap: string | null
      citta: string | null
      provincia: string | null
    } | null
    stesso_indirizzo: boolean
  }
  fornitura: {
    luce: {
      pod: string | null
      potenza: number | null
      consumo_annuo: number | null
      titolarita: string | null
      tipo_attivazione: string | null
      subentro_accettato: boolean
      voltura_accettata: boolean
      vecchio_intestatario: {
        nome: string
        cognome: string
        codice_fiscale: string
      } | null
    } | null
    gas: {
      pdr: string | null
      matricola: string | null
      remi_pdc: string | null
      consumo_annuo: number | null
      titolarita: string | null
      tipo_attivazione: string | null
      subentro_accettato: boolean
      voltura_accettata: boolean
      vecchio_intestatario: {
        nome: string
        cognome: string
        codice_fiscale: string
      } | null
    } | null
  }
  pagamento: {
    metodo: string | null
    iban: string | null
  }
  consensi: {
    privacy: boolean
    marketing: boolean
    comunicazioni: boolean
  }
  metadata: {
    source: string
    ocr_assisted: boolean
    completion_time: number | null
  }
  documents?: GeneratedDocument[]
  created_at: string
  updated_at: string
  submitted_at: string | null
}

// Form step.
// 'upload' (caricamento bolletta + OCR) e' stato rimosso dal flusso a maggio
// 2026: l'operatore inserisce direttamente i dati anagrafici. Il valore resta
// nell'enum solo per non rompere step persistiti in sessionStorage che vengono
// poi rimappati automaticamente da FormWrapper sul primo step valido.
export type FormStep = 'tipo_cliente' | 'upload' | 'anagrafica' | 'documento' | 'fornitura' | 'pagamento' | 'riepilogo' | 'otp_request' | 'otp_verify' | 'firma' | 'completato'

// Steps base del form (tipo_cliente visibile solo in self-service).
// NB: 'upload' rimosso dal flusso.
// 'fornitura' precede 'documento' (set-2026): la tipologia di attivazione
// decide se il documento va allegato, quindi deve essere nota prima.
export const FORM_STEPS: FormStep[] = ['tipo_cliente', 'anagrafica', 'fornitura', 'documento', 'pagamento', 'riepilogo']

// Steps senza tipo_cliente (per operatore che sceglie via navbar).
// NB: 'upload' rimosso dal flusso.
export const FORM_STEPS_OPERATOR: FormStep[] = ['anagrafica', 'fornitura', 'documento', 'pagamento', 'riepilogo']

// Steps per il processo di firma (dopo riepilogo)
export const SIGNING_STEPS: FormStep[] = ['otp_request', 'otp_verify', 'firma', 'completato']

export const STEP_LABELS: Record<FormStep, string> = {
  tipo_cliente: 'Tipo',
  upload: 'Carica',
  anagrafica: 'Anagrafica',
  documento: 'Documento',
  fornitura: 'Fornitura',
  pagamento: 'Pagamento',
  riepilogo: 'Riepilogo',
  otp_request: 'Verifica',
  otp_verify: 'Codice',
  firma: 'Firma',
  completato: 'Completa'
}

/**
 * Returns the correct form steps based on mode.
 * Operator mode skips tipo_cliente (chosen via navbar route).
 */
export function getFormSteps(mode: 'self' | 'operator'): FormStep[] {
  return mode === 'operator' ? FORM_STEPS_OPERATOR : FORM_STEPS
}

// ============================================
// SIGNING TYPES
// ============================================

// Sessione di firma
export interface SigningSession {
  id: string
  submission_id: number
  contact_type: 'email' | 'phone'
  contact_value: string
  client_name: string
  client_surname: string
  document_name: string
  status: 'pending' | 'otp_sent' | 'verified' | 'signing' | 'completed'
  pdf_url: string | null
  ha_luce: boolean
  ha_gas: boolean
  /** VAS "Hera NoProblem" allegati alla pratica (per aggiungere le firme dedicate). */
  vas_luce?: boolean
  vas_gas?: boolean
  created_at: string
  completed_at: string | null
}

// Record di firma
export interface SignatureRecord {
  fieldId: string
  fieldName: string
  signedAt: string
  clientName: string
  clientSurname: string
  ipAddress: string
}

// Campo firma
export interface SignatureField {
  id: string
  name: string
  label: string
  required: boolean
}

// Campo firma con tipo documento (per mappatura 1:1 con PDF)
export interface SignatureFieldConfig extends SignatureField {
  documentType: 'contratto' | 'cte_gas' | 'cte_luce' | 'vas_gas' | 'vas_luce'
}

// ============================================
// CONFIGURAZIONE CAMPI FIRMA PER TIPO CONTRATTO
// ============================================

// Campi firma per CAMBIO FORNITORE (6 firme sul contratto)
// Corrispondono ai 6 campi Data_* nel template Heracomm:
// 1. Data_Recesso (y=485) - Procura al Recesso
// 2. Data_LastCall (y=531) - Accettazione Comunicazioni Variazioni
// 3. Data_Recesso_CF (y=580) - Richiesta Attivazione durante Recesso
// 4. Data_Comunicazioni (y=643) - Consenso Trattamento Dati/Marketing
// 5. Data_Trattamento (y=723) - Adesione Servizio Last Call
// 6. Data_Sottoscrizione (y=796) - Approvazione Clausole Vessatorie
const CAMBIO_FORNITORE_FIELDS: SignatureFieldConfig[] = [
  { id: 'firma_recesso_fornitore', name: 'Recesso Cambio Fornitore', label: 'Procura al Recesso', required: true, documentType: 'contratto' },
  { id: 'firma_comunicazioni', name: 'Comunicazioni Variazioni', label: 'Accettazione Comunicazioni', required: true, documentType: 'contratto' },
  { id: 'firma_richiesta_attivazione', name: 'Richiesta Attivazione', label: 'Richiesta Attivazione durante Recesso', required: true, documentType: 'contratto' },
  { id: 'firma_marketing', name: 'Consenso Marketing', label: 'Consenso Trattamento Dati', required: false, documentType: 'contratto' },
  { id: 'firma_lastcall', name: 'Servizio Last Call', label: 'Adesione Last Call', required: true, documentType: 'contratto' },
  { id: 'firma_clausole', name: 'Clausole Vessatorie', label: 'Approvazione Clausole Vessatorie', required: true, documentType: 'contratto' },
  { id: 'firma_pagamento_sepa', name: 'Firma Pagamento SEPA', label: 'Firma Modalità di Pagamento (SEPA/Conto)', required: true, documentType: 'contratto' },
  { id: 'firma_invio_bolletta', name: 'Firma Modalità Invio Bolletta', label: 'Firma Modalità di Invio della Bolletta', required: true, documentType: 'contratto' },
  { id: 'firma_diario_contatto', name: 'Firma Diario Consumi e Contatto', label: 'Firma Diario dei Consumi e Modalità di Contatto', required: true, documentType: 'contratto' }
]

// Campi firma per SUBENTRO (firme contratto - identiche al cambio fornitore,
// perché il template Heracomm è lo stesso per tutti i tipi di attivazione domestica)
const SUBENTRO_FIELDS: SignatureFieldConfig[] = [
  { id: 'firma_recesso_fornitore', name: 'Recesso Cambio Fornitore', label: 'Procura al Recesso', required: true, documentType: 'contratto' },
  { id: 'firma_comunicazioni', name: 'Comunicazioni Variazioni', label: 'Accettazione Comunicazioni', required: true, documentType: 'contratto' },
  { id: 'firma_richiesta_attivazione', name: 'Richiesta Attivazione', label: 'Richiesta Attivazione durante Recesso', required: true, documentType: 'contratto' },
  { id: 'firma_marketing', name: 'Consenso Marketing', label: 'Consenso Trattamento Dati', required: false, documentType: 'contratto' },
  { id: 'firma_lastcall', name: 'Servizio Last Call', label: 'Adesione Last Call', required: true, documentType: 'contratto' },
  { id: 'firma_clausole', name: 'Clausole Vessatorie', label: 'Approvazione Clausole Vessatorie', required: true, documentType: 'contratto' },
  { id: 'firma_pagamento_sepa', name: 'Firma Pagamento SEPA', label: 'Firma Modalità di Pagamento (SEPA/Conto)', required: true, documentType: 'contratto' },
  { id: 'firma_invio_bolletta', name: 'Firma Modalità Invio Bolletta', label: 'Firma Modalità di Invio della Bolletta', required: true, documentType: 'contratto' },
  { id: 'firma_diario_contatto', name: 'Firma Diario Consumi e Contatto', label: 'Firma Diario dei Consumi e Modalità di Contatto', required: true, documentType: 'contratto' }
]

// Campi firma per VOLTURA (firme contratto - identiche al cambio fornitore,
// perché il template Heracomm è lo stesso per tutti i tipi di attivazione domestica)
const VOLTURA_FIELDS: SignatureFieldConfig[] = [
  { id: 'firma_recesso_fornitore', name: 'Recesso Cambio Fornitore', label: 'Procura al Recesso', required: true, documentType: 'contratto' },
  { id: 'firma_comunicazioni', name: 'Comunicazioni Variazioni', label: 'Accettazione Comunicazioni', required: true, documentType: 'contratto' },
  { id: 'firma_richiesta_attivazione', name: 'Richiesta Attivazione', label: 'Richiesta Attivazione durante Recesso', required: true, documentType: 'contratto' },
  { id: 'firma_marketing', name: 'Consenso Marketing', label: 'Consenso Trattamento Dati', required: false, documentType: 'contratto' },
  { id: 'firma_lastcall', name: 'Servizio Last Call', label: 'Adesione Last Call', required: true, documentType: 'contratto' },
  { id: 'firma_clausole', name: 'Clausole Vessatorie', label: 'Approvazione Clausole Vessatorie', required: true, documentType: 'contratto' },
  { id: 'firma_pagamento_sepa', name: 'Firma Pagamento SEPA', label: 'Firma Modalità di Pagamento (SEPA/Conto)', required: true, documentType: 'contratto' },
  { id: 'firma_invio_bolletta', name: 'Firma Modalità Invio Bolletta', label: 'Firma Modalità di Invio della Bolletta', required: true, documentType: 'contratto' },
  { id: 'firma_diario_contatto', name: 'Firma Diario Consumi e Contatto', label: 'Firma Diario dei Consumi e Modalità di Contatto', required: true, documentType: 'contratto' }
]

// ============================================
// BUSINESS SIGNATURE FIELDS (coordinate PDF da definire con plichi)
// ============================================

const BUSINESS_CAMBIO_FORNITORE_FIELDS: SignatureFieldConfig[] = [
  { id: 'firma_convenzione', name: 'Proposta Contrattuale - Convenzione', label: 'Firma Proposta Contrattuale (Il Legale Rappresentante)', required: true, documentType: 'contratto' },
  { id: 'firma_pagamento_sepa', name: 'Modulo Pagamento - Firma SEPA', label: 'Firma Sottoscrittore Delegato (SEPA)', required: true, documentType: 'contratto' },
  { id: 'firma_recesso_fornitore', name: 'Recesso Cambio Fornitore', label: 'Procura al Recesso', required: true, documentType: 'contratto' },
  { id: 'firma_comunicazioni', name: 'Comunicazioni Variazioni', label: 'Accettazione Comunicazioni', required: true, documentType: 'contratto' },
  { id: 'firma_richiesta_attivazione', name: 'Richiesta Attivazione', label: 'Richiesta Attivazione durante Recesso', required: true, documentType: 'contratto' },
  { id: 'firma_marketing', name: 'Consenso Marketing', label: 'Consenso Trattamento Dati', required: false, documentType: 'contratto' },
  { id: 'firma_lastcall', name: 'Modalità di Contatto', label: 'Modalità di Contatto', required: true, documentType: 'contratto' },
  { id: 'firma_clausole', name: 'Clausole Vessatorie', label: 'Approvazione Clausole Vessatorie', required: true, documentType: 'contratto' }
]

const BUSINESS_SUBENTRO_FIELDS: SignatureFieldConfig[] = [
  { id: 'firma_convenzione', name: 'Proposta Contrattuale - Convenzione', label: 'Firma Proposta Contrattuale (Il Legale Rappresentante)', required: true, documentType: 'contratto' },
  { id: 'firma_pagamento_sepa', name: 'Modulo Pagamento - Firma SEPA', label: 'Firma Sottoscrittore Delegato (SEPA)', required: true, documentType: 'contratto' },
  { id: 'firma_cliente', name: 'Firma del Cliente', label: 'Firma del Cliente / Rappresentante Legale', required: true, documentType: 'contratto' },
  { id: 'firma_clausole', name: 'Clausole Vessatorie', label: 'Approvazione Clausole Vessatorie (Artt. 1341/1342 c.c.)', required: true, documentType: 'contratto' }
]

const BUSINESS_VOLTURA_FIELDS: SignatureFieldConfig[] = [
  { id: 'firma_convenzione', name: 'Proposta Contrattuale - Convenzione', label: 'Firma Proposta Contrattuale (Il Legale Rappresentante)', required: true, documentType: 'contratto' },
  { id: 'firma_pagamento_sepa', name: 'Modulo Pagamento - Firma SEPA', label: 'Firma Sottoscrittore Delegato (SEPA)', required: true, documentType: 'contratto' },
  { id: 'firma_cliente', name: 'Firma del Cliente', label: 'Firma del Cliente / Rappresentante Legale', required: true, documentType: 'contratto' },
  { id: 'firma_disdettante', name: 'Firma Disdettante', label: 'Firma come Disdettante', required: true, documentType: 'contratto' },
  { id: 'firma_clausole', name: 'Clausole Vessatorie', label: 'Approvazione Clausole Vessatorie (Artt. 1341/1342 c.c.)', required: true, documentType: 'contratto' },
  { id: 'firma_marketing', name: 'Consenso Marketing', label: 'Firma Consensi Marketing', required: false, documentType: 'contratto' }
]

// Mappa contratto -> campi firma (domestico)
export const SIGNATURE_FIELDS_BY_CONTRACT: Record<string, SignatureFieldConfig[]> = {
  cambio_fornitore: CAMBIO_FORNITORE_FIELDS,
  subentro: SUBENTRO_FIELDS,
  voltura: VOLTURA_FIELDS
}

// Mappa contratto -> campi firma (business)
export const BUSINESS_SIGNATURE_FIELDS_BY_CONTRACT: Record<string, SignatureFieldConfig[]> = {
  cambio_fornitore: BUSINESS_CAMBIO_FORNITORE_FIELDS,
  subentro: BUSINESS_SUBENTRO_FIELDS,
  voltura: BUSINESS_VOLTURA_FIELDS
}

// Campi firma per le CTE (Condizioni Tecnico Economiche)
export const CTE_SIGNATURE_FIELDS = {
  gas: { id: 'firma_cte_gas', name: 'Firma CTE Gas', label: 'Accettazione Condizioni Gas', required: true, documentType: 'cte_gas' } as SignatureFieldConfig,
  luce: { id: 'firma_cte_luce', name: 'Firma CTE Luce', label: 'Accettazione Condizioni Luce', required: true, documentType: 'cte_luce' } as SignatureFieldConfig
}

// Campi firma per i VAS "Hera NoProblem" (Servizi a Valore Aggiunto).
// Presenti solo quando l'operatore (agenzia Adventure) allega il VAS.
export const VAS_SIGNATURE_FIELDS = {
  gas: { id: 'firma_vas_gas', name: 'Firma VAS Gas', label: 'Adesione Hera NoProblem Gas', required: true, documentType: 'vas_gas' } as SignatureFieldConfig,
  luce: { id: 'firma_vas_luce', name: 'Firma VAS Luce', label: 'Adesione Hera NoProblem Luce', required: true, documentType: 'vas_luce' } as SignatureFieldConfig
}

/**
 * Ottiene i campi firma in base al tipo di contratto, forniture attive e tipo cliente.
 */
export function getSignatureFields(
  contractType: string,
  hasGas: boolean,
  hasLuce: boolean,
  isBusiness: boolean = false,
  vasGas: boolean = false,
  vasLuce: boolean = false
): SignatureFieldConfig[] {
  const fieldMap = isBusiness ? BUSINESS_SIGNATURE_FIELDS_BY_CONTRACT : SIGNATURE_FIELDS_BY_CONTRACT
  const fallback = isBusiness ? BUSINESS_CAMBIO_FORNITORE_FIELDS : CAMBIO_FORNITORE_FIELDS
  const contractFields = fieldMap[contractType] || fallback
  const fields = [...contractFields]
  
  if (hasGas) {
    fields.push(CTE_SIGNATURE_FIELDS.gas)
  }
  
  if (hasLuce) {
    fields.push(CTE_SIGNATURE_FIELDS.luce)
  }

  // I VAS "Hera NoProblem" (se allegati) vanno firmati in coda, dopo le CTE.
  if (vasGas) {
    fields.push(VAS_SIGNATURE_FIELDS.gas)
  }

  if (vasLuce) {
    fields.push(VAS_SIGNATURE_FIELDS.luce)
  }
  
  return fields
}

/**
 * Determina il tipo di contratto dai dati della submission.
 * 
 * @param submission - Dati della submission
 * @returns Tipo contratto: 'cambio_fornitore', 'subentro', 'voltura'
 */
export function getContractTypeFromSubmission(submission: Partial<Submission> | null): string {
  if (!submission) return 'cambio_fornitore'
  
  const fornitura = submission.fornitura
  if (!fornitura) return 'cambio_fornitore'
  
  // Controlla tipo attivazione luce
  const luceActivation = fornitura.luce?.tipo_attivazione
  if (luceActivation === 'subentro') return 'subentro'
  if (luceActivation === 'cambio_fornitore_voltura') return 'voltura'
  
  // Controlla tipo attivazione gas
  const gasActivation = fornitura.gas?.tipo_attivazione
  if (gasActivation === 'subentro') return 'subentro'
  if (gasActivation === 'cambio_fornitore_voltura') return 'voltura'
  
  return 'cambio_fornitore'
}

// Legacy: mantieni SIGNATURE_FIELDS per retrocompatibilità
// Usa getSignatureFields() per nuove implementazioni
export const SIGNATURE_FIELDS: SignatureField[] = CAMBIO_FORNITORE_FIELDS

// Documento firmato
export interface SignedDocument {
  id: string
  session_id: string
  signatures: SignatureRecord[]
  completed_at: string | null
  completed: boolean
}

// Camera state
export interface CameraState {
  isOpen: boolean
  isLoading: boolean
  hasPermission: boolean | null
  error: string | null
  stream: MediaStream | null
  capturedImage: string | null
}

// Copertura Fornitura (risultato verifica comune)
export interface CoperturaFornitura {
  verificato: boolean
  comune: string
  provincia: string
  luce_disponibile: boolean
  gas_disponibile: boolean
  /** Da heracomm_comuni.societa_ee (HC / EENE) dopo verifica API */
  societa_ee?: string | null
  /** Derivato: solo EENE → estenergy, altrimenti heracomm */
  company?: 'heracomm' | 'estenergy' | null
}

// Store types
export interface FormStore {
  data: FormData
  step: FormStep
  submissionId: number | null
  ocrAssisted: boolean
  isDirty: boolean
  mode: 'self' | 'operator'
  
  // Signing state
  signingSessionId: string | null
  signingSession: SigningSession | null
  signatures: SignatureRecord[]
  generatedDocuments: GeneratedDocument[]
  
  // Identity documents state
  identityDocumentsStatus: IdentityDocumentsStatus | null
  
  // Copertura territoriale
  coperturaFornitura: CoperturaFornitura | null
  
  // Actions
  setData: (data: Partial<FormData>) => void
  setStep: (step: FormStep) => void
  setSubmissionId: (id: number) => void
  setOcrAssisted: (value: boolean) => void
  setMode: (mode: 'self' | 'operator') => void
  setSigningSessionId: (id: string | null) => void
  setSigningSession: (session: SigningSession | null) => void
  addSignature: (signature: SignatureRecord) => void
  setSignatures: (signatures: SignatureRecord[]) => void
  setGeneratedDocuments: (docs: GeneratedDocument[]) => void
  setIdentityDocumentsStatus: (status: IdentityDocumentsStatus | null) => void
  setCoperturaFornitura: (copertura: CoperturaFornitura | null) => void
  resetForm: () => void
  nextStep: () => void
  prevStep: () => void
}

/**
 * Helper per determinare se gli ESTREMI del documento identità (tipo, numero,
 * date, ente) sono obbligatori.
 * In modalità operatore: sempre richiesti.
 * Business self-service: non richiesti.
 * Domestico self-service: richiesti per switch (cambio fornitore), voltura e subentro.
 *
 * Per l'allegato fronte/retro vedi isIdentityAttachmentRequired.
 */
export function isIdentityDocumentRequired(
  formData: Partial<FormData>,
  mode: 'self' | 'operator' = 'self'
): boolean {
  if (mode === 'operator') return true

  if (formData.tipo_cliente === 'business') return false

  const tipoLuce = formData.tipo_attivazione_luce
  const tipoGas = formData.tipo_attivazione_gas

  const requiringTypes: Array<TipoAttivazioneLuce | TipoAttivazioneGas> = [
    'cambio_fornitore',
    'cambio_fornitore_voltura',
    'subentro',
  ]

  if (
    (tipoLuce && requiringTypes.includes(tipoLuce)) ||
    (tipoGas && requiringTypes.includes(tipoGas))
  ) {
    return true
  }

  return false
}

/**
 * Data (Europe/Rome) dalla quale l'allegato fronte/retro del documento non è più
 * obbligatorio per le pratiche di solo switch. Deve restare allineata alla
 * costante IDENTITY_ATTACHMENT_SWITCH_EXEMPTION_FROM in
 * backend/lib/IdentityDocumentRules.php.
 */
export const IDENTITY_ATTACHMENT_SWITCH_EXEMPTION_FROM = '2026-09-21'

/**
 * Data odierna come 'AAAA-MM-GG' nel fuso Europe/Rome: così la regola cambia
 * nello stesso istante del backend anche su browser con fuso diverso.
 */
function getRomeDate(now: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

/**
 * Tipologie di attivazione delle sole forniture effettivamente richieste: ignora
 * i valori residui su una fornitura deselezionata.
 */
function getTipologieAttivazioneAttive(
  formData: Partial<FormData>
): Array<TipoAttivazioneLuce | TipoAttivazioneGas> {
  const tipologie: Array<TipoAttivazioneLuce | TipoAttivazioneGas> = []

  if (formData.ha_luce && formData.tipo_attivazione_luce) {
    tipologie.push(formData.tipo_attivazione_luce)
  }
  if (formData.ha_gas && formData.tipo_attivazione_gas) {
    tipologie.push(formData.tipo_attivazione_gas)
  }

  return tipologie
}

/**
 * Helper per determinare se l'ALLEGATO fronte/retro del documento è obbligatorio.
 *
 * Dal 21/09/2026 le pratiche di solo switch (cambio fornitore) ne sono esentate:
 * restano obbligatori gli estremi del documento, non la copia. Voltura e subentro
 * richiedono l'allegato anche quando l'altra fornitura è uno switch.
 *
 * Lo step Fornitura precede Documento, quindi quando questo helper serve a
 * decidere cosa mostrare la tipologia è già nota. Se non lo fosse (dati
 * incompleti) non pretendiamo l'allegato: la verifica definitiva è nel backend.
 */
export function isIdentityAttachmentRequired(
  formData: Partial<FormData>,
  mode: 'self' | 'operator' = 'self',
  now: Date = new Date()
): boolean {
  if (!isIdentityDocumentRequired(formData, mode)) return false

  if (getRomeDate(now) < IDENTITY_ATTACHMENT_SWITCH_EXEMPTION_FROM) return true

  const requiringAttachment: Array<TipoAttivazioneLuce | TipoAttivazioneGas> = [
    'cambio_fornitore_voltura',
    'subentro',
  ]

  return getTipologieAttivazioneAttive(formData).some((tipologia) =>
    requiringAttachment.includes(tipologia)
  )
}

// ============================================
// COPERTURA TERRITORIALE TYPES
// ============================================

export interface CoperturaComune {
  comune: string
  provincia: string
  regione: string
  cap: string
  luce_disponibile: boolean
  gas_disponibile: boolean
  societa_ee?: string
  company?: 'heracomm' | 'estenergy'
  label?: string
}

export interface CoperturaResponse {
  trovato: boolean
  comune: string
  provincia: string | null
  regione?: string
  cap?: string
  luce_disponibile: boolean
  gas_disponibile: boolean
  societa_ee?: string | null
  company?: 'heracomm' | 'estenergy'
  messaggio: string
}

export interface CoperturaSearchResponse {
  comuni: CoperturaComune[]
  totale: number
}

// Component Props
export interface InputProps {
  label: string
  name: string
  type?: 'text' | 'email' | 'tel' | 'number' | 'date'
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  hint?: string
  maxLength?: number
  pattern?: string
  autoComplete?: string
  icon?: string
  uppercase?: boolean
  className?: string
}

export interface ButtonProps {
  children: React.ReactNode
  type?: 'button' | 'submit'
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  icon?: string
  iconPosition?: 'left' | 'right'
  loading?: boolean
  disabled?: boolean
  fullWidth?: boolean
  onClick?: () => void
  className?: string
}

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'fullscreen'
  showClose?: boolean
  className?: string
}

// ============================================
// OPERATOR MODE TYPES
// ============================================

// Modalità di compilazione form
export type FormMode = 'self' | 'operator'

// Stato complessivo pratica per operatore
export interface OperatorSubmissionStatus {
  status: 'draft' | 'sent' | 'opened' | 'signing' | 'completed'
  label: string
  description: string
  progress: number
}

// Risposta invio link al cliente
export interface SendToClientResponse {
  submission_id: number
  sent_to: string
  sent_at?: string
  resent_at?: string
}

// Dati fornitura per operatore
export interface OperatorFornituraDetail {
  ha_luce: boolean
  ha_gas: boolean
  tipo_fornitura: string | null
  pod_luce: string | null
  pdr_gas: string | null
  tipo_attivazione_luce: string | null
  tipo_attivazione_gas: string | null
  indirizzo_fornitura_luce: string | null
  civico_fornitura_luce: string | null
  citta_fornitura_luce: string | null
  provincia_fornitura_luce: string | null
  cap_fornitura_luce: string | null
  indirizzo_fornitura_gas: string | null
  civico_fornitura_gas: string | null
  citta_fornitura_gas: string | null
  provincia_fornitura_gas: string | null
  cap_fornitura_gas: string | null
  codice_prodotto_luce: string | null
  codice_prodotto_gas: string | null
}

// Stato dettagliato pratica per operatore
export interface OperatorSubmissionDetail {
  submission: {
    id: number
    uuid: string
    status: string
    mode: FormMode
    tipo_cliente: TipoCliente
    created_at: string
    updated_at: string
    submitted_at: string | null
    operator_username: string | null
    sidial_order_id: number | null
  }
  client: {
    nome: string
    cognome: string
    email: string
    telefono: string
    codice_fiscale: string
  }
  fornitura: OperatorFornituraDetail
  operator: {
    link_sent_at: string | null
    link_opened_at: string | null
    notes: string | null
  }
  signing: {
    session_id: string | null
    session_status: string | null
    signatures_count: number
    completed_at: string | null
    signed_pdf_available: boolean
  }
  overall_status: OperatorSubmissionStatus
}

// ============================================
// CLIENT SIGNING TYPES
// ============================================

// Submission formattata per cliente
export interface ClientSubmission {
  id: number
  uuid: string
  status: string
  tipo_cliente: TipoCliente
  client_must_upload_identity: boolean
  prodotto_luce_id: number | null
  prodotto_gas_id: number | null
  anagrafica: {
    nome: string
    cognome: string
    codice_fiscale: string
    data_nascita: string | null
    luogo_nascita: string | null
    telefono: string
    email: string
  }
  indirizzo: {
    residenza: {
      indirizzo: string
      cap: string
      citta: string
      provincia: string
    }
    fornitura: {
      indirizzo: string
      cap: string
      citta: string
      provincia: string
    } | null
    stesso_indirizzo: boolean
  }
  tipo_fornitura: 'luce' | 'gas' | 'entrambi'
  fornitura: {
    luce: {
      pod: string
      potenza: number | null
      tipo_attivazione: string | null
      prodotto_id: number | null
    } | null
    gas: {
      pdr: string
      matricola: string | null
      tipo_attivazione: string | null
      prodotto_id: number | null
    } | null
  }
  pagamento: {
    metodo: string | null
    iban: string | null
    bic_swift?: string | null
  }
  consensi: {
    privacy: boolean
    marketing: boolean
    comunicazioni: boolean
  }
  created_at: string
}

// Risposta GET /client-signing/:token
export interface ClientSigningResponse {
  submission: ClientSubmission
  signing: {
    session_exists: boolean
    session_id: string | null
    session_status: string | null
  }
  documents: Array<{
    filename: string
    url: string
  }>
  can_edit_fields: string[]
}

// Risposta POST /client-signing/:token/start
export interface StartClientSigningResponse {
  session: {
    id: string
    submission_id: number
    status: string
    contact_type: 'email' | 'phone'
    document_name: string
    pdf_url: string | null
    ha_luce: boolean
    ha_gas: boolean
    created_at: string
  }
  otp: {
    sent: boolean
    expires_at: string
    contact_masked: string
    _debug_code?: string
  }
}

